import { createTheme, alpha } from '@mui/material/styles';
import { ACCENT, SERIF } from './data/studioStory';

// Function to create theme based on mode (light or dark)
export const createAppTheme = (mode = 'dark') => {
  const isDark = mode === 'dark';

  // One focus ring for the whole site. Buttons previously fell back to MUI's
  // ripplePulsate — a translucent ellipse painted OVER the label — while the
  // header links drew a 2px outline, so two controls 20px apart announced focus
  // in two different visual languages and the less legible one was on the
  // primary action. Same value as primary.main; kept as its own const so the
  // ring is set in one place rather than per component.
  const FOCUS_RING = isDark ? '#667eea' : '#5468d4';

  return createTheme({
    palette: {
      mode: mode,
      primary: {
        // Indigo matching the chat gradient. Two shades because one can't clear
        // 4.5:1 against both backdrops: #667eea is 4.5:1 on the dark ground but
        // only 3.7:1 on white, so light mode uses the deeper #5468d4 (4.9:1).
        main: isDark ? '#667eea' : '#5468d4',
      },
      secondary: {
        main: '#764ba2', // Purple shade matching the gradient
      },
      // Warm-orange brand accent shared with the Skylark /t/arch aesthetic.
      // Used on the studio-story surfaces (home + /studio). Reachable in sx via
      // color: 'accent.main'.
      // contrastText is near-black, not white: white on the vivid orange is
      // 2.9:1. Dark-on-orange is 6.1:1 and keeps the brand hue untouched.
      accent: {
        main: ACCENT,
        contrastText: '#1a1a1a',
        // The vivid orange is only 2.9:1 on white, so small accent TEXT needs a
        // deeper shade in light mode. accent.main stays the brand hue for fills,
        // borders and diagram strokes; accent.text is for anything you read.
        text: isDark ? ACCENT : 'hsl(26 95% 35%)',
      },
      background: {
        default: isDark ? '#111827' : '#ffffff',
        paper: isDark ? '#1f2937' : '#ffffff'
      },
      text: {
        primary: isDark ? '#ffffff' : '#1a1a1a',
        secondary: isDark ? alpha('#ffffff', 0.7) : '#666666',
      },
    },
    shape: {
      borderRadius: 8, // Consistent border radius across components
    },
    typography: {
      // IBM Plex Sans, not Roboto. Roboto is MUI's default, which reads as
      // "the framework picked the type" to anyone who builds for the web.
      fontFamily: '"IBM Plex Sans", "Helvetica", "Arial", sans-serif',
      // Editorial serif for display headings (h1-h4), elevating toward the
      // /t/arch aesthetic. Body + smaller headings stay in the sans stack.
      h1: {
        fontFamily: SERIF,
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h2: {
        fontFamily: SERIF,
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontFamily: SERIF,
        fontWeight: 600,
      },
      h4: {
        fontFamily: SERIF,
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
    },
    // Consistent transition timing
    transitions: {
      easing: {
        smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
        spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      },
      duration: {
        short: 150,
        standard: 300,
        long: 500,
      },
    },
    components: {
      // Customize component defaults
      MuiButton: {
        // Kill the FOCUS pulsate only. MUI's Button passes
        // focusRipple={!disableFocusRipple}, so keyboard focus painted a
        // translucent ellipse over the label; the outline below replaces it.
        // The touch/click ripple stays — that one is press feedback, and
        // removing it would trade an accessibility fix for a responsiveness
        // regression. (disableTouchRipple is the opposite lever and does not
        // affect the pulsate.)
        defaultProps: {
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            minHeight: 44, // tap-target floor; MUI's default lands at ~36.5px
            borderRadius: 8,
            textTransform: 'none', // Preserve button text casing
            fontWeight: 600,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:focus-visible': {
              outline: `2px solid ${FOCUS_RING}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:focus-visible': {
              outline: `2px solid ${FOCUS_RING}`,
              outlineOffset: 2,
              borderRadius: 2,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:focus-visible': {
              outline: `2px solid ${FOCUS_RING}`,
              outlineOffset: 2,
            },
          },
        },
      },
    },
  });
};

// Default export for backwards compatibility
export default createAppTheme('dark');
